import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import { Activity, CheckCheck, XCircle, Percent, Lock, AtSign, ShieldCheck, KeyRound, ClipboardCheck } from "lucide-react";

const META = {
  "merchant.approve": { icon: CheckCheck, color: "bg-emerald-50 text-emerald-700", label: "Approved merchant" },
  "merchant.reject": { icon: XCircle, color: "bg-rose-50 text-rose-700", label: "Rejected merchant" },
  "order.complete": { icon: ClipboardCheck, color: "bg-emerald-50 text-emerald-700", label: "Completed order" },
  "admin.commission.update": { icon: Percent, color: "bg-amber-50 text-amber-700", label: "Updated commission" },
  "admin.password.change": { icon: Lock, color: "bg-zinc-100 text-zinc-700", label: "Changed admin password" },
  "admin.owner_email.change": { icon: AtSign, color: "bg-zinc-100 text-zinc-700", label: "Changed owner email" },
  "admin.tfa.toggle": { icon: ShieldCheck, color: "bg-zinc-100 text-zinc-700", label: "Toggled 2FA" },
  "admin.login": { icon: KeyRound, color: "bg-emerald-50 text-emerald-700", label: "Admin signed in" },
};

function actionInfo(a) {
  return META[a] || { icon: Activity, color: "bg-zinc-100 text-zinc-700", label: a };
}

function MetaLine({ action, meta, target }) {
  if (!meta && !target) return null;
  if (action === "admin.commission.update") return <>Rate: <b>{meta.rate}%</b></>;
  if (action === "order.complete") return <>Price: <b>{meta.price} SAR</b> · Commission: <b>{meta.commission} SAR</b></>;
  if (action === "admin.tfa.toggle") return <>2FA: <b>{meta.enabled ? "on" : "off"}</b></>;
  if (action === "admin.owner_email.change") return <>New: <b>{meta.new_email}</b></>;
  if (action?.startsWith("merchant.")) return <>Merchant id: <code className="text-[11px]">{target?.slice(0, 8)}…</code></>;
  if (action === "admin.login") return <>{meta?.method ? `via ${meta.method}` : null}</>;
  return null;
}

export default function AdminActivity() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/activity").then((r) => setItems(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);

  return (
    <div className="app-shell pb-24" data-testid="admin-activity-page">
      <TopBar title="Activity log" />
      <div className="px-5 pt-4">
        <h1 className="font-display text-2xl font-bold">Activity</h1>
        <p className="text-sm text-zinc-500 mt-1">Every admin action, oldest at the bottom. Tamper-evident.</p>

        {err && <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg p-3">{err}</div>}
        {!err && items.length === 0 && <div className="card-surface p-5 text-sm text-zinc-600 mt-4">No activity yet.</div>}

        <div className="mt-4 space-y-2">
          {items.map((it) => {
            const info = actionInfo(it.action);
            const Icon = info.icon;
            return (
              <div key={it.id} className="card-surface p-3 flex items-start gap-3" data-testid={`log-${it.id}`}>
                <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 ${info.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">{info.label}</div>
                    <div className="text-[10px] text-zinc-400 shrink-0">{new Date(it.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5"><MetaLine action={it.action} meta={it.meta} target={it.target} /></div>
                  <div className="text-[10px] text-zinc-400 mt-1">{it.actor_email}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
